import { REQUESTED_COL_KEY, INCUMBENT_COL_KEY } from "../tabs/Comps/types";

import type { CellReviewResponseRecord } from "../tabs/shared/cellReviews/useCellReviewStore";

/**
 * One resolved "Needs Review" comment, shaped per its source page. A
 * `cell_reviews` row reuses generic columns per scope (see
 * `CellReview` ent schema) — this is where that reuse gets untangled into a
 * vocabulary specific to each page.
 */
export type ResolvedComment =
  | {
      source: "quotes";
      id: string;
      carrierId: string;
      comment: string;
      proposalPlanId: string;
      /** Stored LOC coverage type, e.g. "dental-ppo" — selects which coverage's
       * benefit definitions order and label this comment's `benefitKey`.
       * Quotes/SBS variants (dental-ppo/dental-hmo) are stored separately from
       * the base "dental" coverage type Responses/Comps use. */
      coverageType: string;
      /** The RFP-requested plan group (SBS page) this proposal plan answers —
       * resolves the heading/plan-group-card grouping the same way a
       * Responses comment does. */
      planGroupId: string;
      benefitKey: string;
      network: string;
    }
  | {
      source: "responses";
      id: string;
      carrierId: string;
      comment: string;
      planGroupId: string;
    }
  | {
      source: "comps";
      id: string;
      carrierId: string;
      comment: string;
      /** `${planGroupId}` for a coverage row, or `subsidy:${kind}` / `notes`. */
      rowGroupId: string;
      rowType: string;
    };

export interface UnresolvedComment {
  id: string;
  reason: string;
}

export interface ResolveCommentsResult {
  comments: ResolvedComment[];
  unresolved: UnresolvedComment[];
}

export interface ProposalPlanRef {
  carrierId: string;
  /** The RFP-requested plan group this proposal plan answers. Falls back to
   * the proposal's own plan-group id for an off-script group with no
   * `rfpBenefitPlanGroupId`, mirroring the BFF's
   * `buildPlanGroupIdByProposalPlanId` (comps-table.ts). */
  planGroupId: string;
}

export interface ResolveCommentsInput {
  quotesReviews: readonly CellReviewResponseRecord[];
  responsesReviews: readonly CellReviewResponseRecord[];
  compsReviews: readonly CellReviewResponseRecord[];
  /** proposalPlanId -> {carrierId, planGroupId}, from `useProposals`.
   * Requested/Current SBS columns have no proposal plan and so are absent —
   * their comments resolve to nothing and are dropped, by design (they have
   * no carrier sales rep). */
  proposalPlanById: ReadonlyMap<string, ProposalPlanRef>;
}

const hasReviewTypeNeedsReview = (review: CellReviewResponseRecord): boolean =>
  (review.reviewType ?? "needs_review") === "needs_review";

const commentTextOf = (review: CellReviewResponseRecord): string | undefined =>
  review.comment?.trim() || undefined;

const NON_CARRIER_COMPS_COLUMNS: ReadonlySet<string> = new Set([
  REQUESTED_COL_KEY,
  INCUMBENT_COL_KEY,
]);

const resolveQuotesComments = (
  reviews: readonly CellReviewResponseRecord[],
  proposalPlanById: ReadonlyMap<string, ProposalPlanRef>,
): ResolveCommentsResult => {
  const comments: ResolvedComment[] = [];
  const unresolved: UnresolvedComment[] = [];

  for (const review of reviews) {
    if (!hasReviewTypeNeedsReview(review)) continue;
    const comment = commentTextOf(review);
    if (!comment) continue;

    const proposalPlanId = review.benefitPlanId;
    const proposalPlan = proposalPlanById.get(proposalPlanId);
    if (!proposalPlan) {
      unresolved.push({
        id: review.id ?? proposalPlanId,
        reason: `quotes comment on unresolved proposal plan ${proposalPlanId}`,
      });
      continue;
    }

    comments.push({
      source: "quotes",
      id: review.id ?? `${proposalPlanId}:${review.benefitKey}`,
      carrierId: proposalPlan.carrierId,
      comment,
      proposalPlanId,
      coverageType: review.coverageType ?? "",
      planGroupId: proposalPlan.planGroupId,
      benefitKey: review.benefitKey,
      network: review.network ?? "",
    });
  }

  return { comments, unresolved };
};

const resolveResponsesComments = (
  reviews: readonly CellReviewResponseRecord[],
): ResolveCommentsResult => {
  const comments: ResolvedComment[] = [];
  const unresolved: UnresolvedComment[] = [];

  for (const review of reviews) {
    if (!hasReviewTypeNeedsReview(review)) continue;
    const comment = commentTextOf(review);
    if (!comment) continue;

    const carrierId = review.benefitKey;
    const planGroupId = review.coverageType;
    if (!carrierId || !planGroupId) {
      unresolved.push({
        id: review.id ?? "",
        reason: "responses comment missing carrier or plan group",
      });
      continue;
    }

    comments.push({
      source: "responses",
      id: review.id ?? `${carrierId}:${planGroupId}`,
      carrierId,
      comment,
      planGroupId,
    });
  }

  return { comments, unresolved };
};

const resolveCompsComments = (
  reviews: readonly CellReviewResponseRecord[],
): ResolveCommentsResult => {
  const comments: ResolvedComment[] = [];
  const unresolved: UnresolvedComment[] = [];

  for (const review of reviews) {
    if (!hasReviewTypeNeedsReview(review)) continue;
    const comment = commentTextOf(review);
    if (!comment) continue;

    const columnKey = review.benefitKey;
    if (NON_CARRIER_COMPS_COLUMNS.has(columnKey)) continue;

    const rowGroupId = review.coverageType;
    const rowType = review.network;
    if (!columnKey || !rowGroupId || !rowType) {
      unresolved.push({
        id: review.id ?? "",
        reason: "comps comment missing column, row group, or row type",
      });
      continue;
    }

    comments.push({
      source: "comps",
      id: review.id ?? `${columnKey}:${rowGroupId}:${rowType}`,
      carrierId: columnKey,
      comment,
      rowGroupId,
      rowType,
    });
  }

  return { comments, unresolved };
};

const mergeResults = (
  results: readonly ResolveCommentsResult[],
): ResolveCommentsResult => ({
  comments: results.flatMap((result) => result.comments),
  unresolved: results.flatMap((result) => result.unresolved),
});

/**
 * Resolves the raw cell-review rows from all three scopes into one flat,
 * per-source list of carrier-attributed comments, dropping (and tallying)
 * comments that resolve to no carrier: Comps' Requested/Incumbent columns,
 * and Quotes/SBS comments on the Requested or Current plan columns (which
 * have no proposal plan and so no entry in `proposalPlanById`).
 */
export const resolveComments = ({
  quotesReviews,
  responsesReviews,
  compsReviews,
  proposalPlanById,
}: ResolveCommentsInput): ResolveCommentsResult =>
  mergeResults([
    resolveQuotesComments(quotesReviews, proposalPlanById),
    resolveResponsesComments(responsesReviews),
    resolveCompsComments(compsReviews),
  ]);
